import React from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import { useParams } from 'react-router-dom';

const backendBaseUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.virtus-one.com';

const useStyles = makeStyles(() => ({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'stretch',
    background: 'radial-gradient(circle at top, #f7fbff 0%, #e8f1fb 44%, #d9e4f0 100%)',
    padding: '24px 0 36px',
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  card: {
    width: '100%',
    maxWidth: 560,
    borderRadius: 28,
    padding: '28px 22px',
    background: 'rgba(255, 255, 255, 0.96)',
    boxShadow: '0 24px 60px rgba(0, 45, 85, 0.14)',
    border: '1px solid rgba(0, 45, 85, 0.08)',
  },
  eyebrow: {
    color: '#0b6aa2',
    fontWeight: 700,
    fontSize: '0.75rem',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    marginBottom: 10,
  },
  title: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: 'clamp(1.45rem, 5vw, 2rem)',
    lineHeight: 1.1,
    marginBottom: 10,
  },
  subtitle: {
    color: '#526579',
    fontSize: '0.94rem',
    lineHeight: 1.65,
    marginBottom: 18,
  },
  statusRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 20,
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 30,
    padding: '6px 12px',
    borderRadius: 999,
    fontWeight: 700,
    fontSize: '0.8rem',
  },
  infoText: {
    color: '#526579',
    fontSize: '0.84rem',
    lineHeight: 1.55,
  },
  previewPanel: {
    marginTop: 18,
    marginBottom: 22,
    borderRadius: 22,
    overflow: 'hidden',
    border: '1px solid #d8e4ef',
    background: 'linear-gradient(180deg, #f9fbfd 0%, #eef4fa 100%)',
    minHeight: 220,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  previewImage: {
    width: '100%',
    maxHeight: 420,
    objectFit: 'cover',
    display: 'block',
  },
  previewVideo: {
    width: '100%',
    display: 'block',
    backgroundColor: '#000000',
  },
  emptyPreview: {
    padding: '24px 20px',
    textAlign: 'center',
  },
  actionGroup: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 12,
  },
  primaryButton: {
    borderRadius: 16,
    minHeight: 56,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.98rem',
    boxShadow: 'none',
    backgroundColor: '#002D55',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#0b6aa2',
      boxShadow: 'none',
    },
  },
  secondaryButton: {
    borderRadius: 16,
    minHeight: 52,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '0.95rem',
    borderColor: '#0b6aa2',
    color: '#0b6aa2',
  },
  errorText: {
    color: '#b3261e',
    fontWeight: 600,
    fontSize: '0.85rem',
    marginTop: 10,
  },
  footerText: {
    marginTop: 18,
    color: '#6a7b8d',
    fontSize: '0.78rem',
    lineHeight: 1.55,
  },
  hiddenInput: {
    display: 'none',
  },
}));

function getApiErrorMessage(response, fallbackMessage) {
  return response
    .json()
    .then((payload) => payload?.detail || payload?.error || fallbackMessage)
    .catch(() => fallbackMessage);
}

function formatExpiryLabel(value) {
  if (!value) {
    return '';
  }

  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
}

function isUuidLike(value) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(String(value || '').trim());
}

function buildUploadApiPaths(identifier) {
  const normalized = String(identifier || '').trim();
  if (isUuidLike(normalized)) {
    return {
      detailPath: `${backendBaseUrl}/treatment/exercise-media-handoffs/public/${normalized}/`,
      uploadPath: `${backendBaseUrl}/treatment/exercise-media-handoffs/public/${normalized}/upload/`,
    };
  }

  return {
    detailPath: `${backendBaseUrl}/treatment/exercise-media-handoffs/${normalized}/`,
    uploadPath: `${backendBaseUrl}/treatment/exercise-media-handoffs/${normalized}/upload/`,
  };
}

export default function ExerciseMediaUploadPage() {
  const classes = useStyles();
  const { token: handoffIdentifier } = useParams();
  const imageInputRef = React.useRef(null);
  const videoInputRef = React.useRef(null);
  const [handoff, setHandoff] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [uploadingType, setUploadingType] = React.useState('');
  const [error, setError] = React.useState('');
  const apiPaths = React.useMemo(() => buildUploadApiPaths(handoffIdentifier), [handoffIdentifier]);

  const loadHandoff = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setLoading(true);
    }

    try {
      const response = await fetch(apiPaths.detailPath);
      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Unable to load upload handoff.'));
      }
      const payload = await response.json();
      setHandoff(payload);
      setError('');
      return payload;
    } catch (requestError) {
      setError(requestError.message || 'Unable to load upload handoff.');
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [apiPaths.detailPath]);

  React.useEffect(() => {
    loadHandoff();
  }, [loadHandoff]);

  const handleFileSelected = React.useCallback(async (mediaType, event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }

    setUploadingType(mediaType);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('media_type', mediaType);

      const response = await fetch(apiPaths.uploadPath, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error(await getApiErrorMessage(response, 'Upload failed.'));
      }

      const payload = await response.json();
      setHandoff(payload);
      setError('');
    } catch (requestError) {
      setError(requestError.message || 'Upload failed.');
      await loadHandoff({ silent: true });
    } finally {
      setUploadingType('');
    }
  }, [apiPaths.uploadPath, loadHandoff]);

  const statusColor = handoff?.status === 'completed'
    ? '#2e7d32'
    : handoff?.status === 'rejected' || handoff?.status === 'failed' || handoff?.is_expired
      ? '#b3261e'
      : '#0b6aa2';
  const statusLabel = handoff?.status === 'completed'
    ? 'Completed'
    : handoff?.status === 'scanning'
      ? 'Scanning upload'
      : handoff?.status === 'rejected'
        ? 'Upload rejected'
        : handoff?.status === 'failed'
          ? 'Scan unavailable'
          : handoff?.is_expired
            ? 'Expired'
            : 'Ready for upload';

  const previewUrl = handoff?.media_url || handoff?.current_picture || handoff?.current_video || '';
  const previewType = handoff?.uploaded_media_type || (handoff?.current_video ? 'video' : handoff?.current_picture ? 'image' : '');
  const canUpload = Boolean(handoff?.can_upload && !uploadingType);

  return (
    <Box className={classes.root}>
      <Container maxWidth="sm" className={classes.container}>
        <Paper className={classes.card} elevation={0}>
          <Typography className={classes.eyebrow}>Exercise Media Upload</Typography>
          <Typography className={classes.title}>
            {handoff?.exercise_title || 'Attach media to exercise'}
          </Typography>
          <Typography className={classes.subtitle}>
            Use your phone to take a picture or record a video, then send it straight back to the clinician's exercise editor.
          </Typography>

          {loading ? (
            <Box style={{ display: 'flex', justifyContent: 'center', padding: '42px 0 28px' }}>
              <CircularProgress style={{ color: '#002D55' }} />
            </Box>
          ) : (
            <>
              <Box className={classes.statusRow}>
                <Box className={classes.statusChip} style={{ color: statusColor, backgroundColor: `${statusColor}14` }}>
                  {statusLabel}
                </Box>
                {handoff?.expires_at && (
                  <Typography className={classes.infoText}>
                    Expires: {formatExpiryLabel(handoff.expires_at)}
                  </Typography>
                )}
              </Box>

              <Box className={classes.previewPanel}>
                {previewUrl ? (
                  previewType === 'video' ? (
                    <video controls preload="metadata" className={classes.previewVideo}>
                      <source src={previewUrl} />
                    </video>
                  ) : (
                    <img src={previewUrl} alt={handoff?.exercise_title || 'Exercise media'} className={classes.previewImage} />
                  )
                ) : (
                  <Box className={classes.emptyPreview}>
                    <Typography style={{ color: '#002D55', fontWeight: 700, marginBottom: 8 }}>
                      No upload received yet
                    </Typography>
                    <Typography className={classes.infoText}>
                      Choose one of the actions below to capture media for this exercise.
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box className={classes.actionGroup}>
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className={classes.hiddenInput}
                  onChange={(event) => handleFileSelected('image', event)}
                />
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  capture="environment"
                  className={classes.hiddenInput}
                  onChange={(event) => handleFileSelected('video', event)}
                />
                <Button
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={() => imageInputRef.current && imageInputRef.current.click()}
                  disabled={!canUpload}
                >
                  {uploadingType === 'image' ? 'Uploading photo...' : 'Take photo or choose image'}
                </Button>
                <Button
                  variant="outlined"
                  className={classes.secondaryButton}
                  onClick={() => videoInputRef.current && videoInputRef.current.click()}
                  disabled={!canUpload}
                >
                  {uploadingType === 'video' ? 'Uploading video...' : 'Record video or choose file'}
                </Button>
                <Button
                  variant="text"
                  className={classes.secondaryButton}
                  onClick={() => loadHandoff()}
                  disabled={Boolean(uploadingType)}
                >
                  Refresh status
                </Button>
              </Box>

              {error && <Typography className={classes.errorText}>{error}</Typography>}

              <Typography className={classes.footerText}>
                Keep this page open until the upload finishes. Once completed, the clinician's exercise dialog will detect the new media automatically.
              </Typography>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
}
